import React, { Suspense, lazy, ReactElement, useMemo } from "react";
import { Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import { PageLayout } from "./components/layout";
import type { PageLayoutProps } from "./components/layout/PageLayout";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import { VerificationPage, getAgentSession } from "./components/verification";
import { mockRootProps } from "./data/verificationMockData";
import { AXIOS_GENERATE_PACKET_TIMEOUT } from "./services/AxiosClient";
import { SuspenseLoader } from "./components/common";
import { useAuth } from "./contexts/AuthContext";

// Lazy loaded pages (code-splitting). Keep above-the-fold verification inline.
const WelcomePage = lazy(() => import("./pages/WelcomePage"));
const AgentAffiliationFormPage = lazy(() => import("./pages/AgentAffiliationFormPage"));
const SupportPage = lazy(() => import("./pages/SupportPage"));
const ErrorPage = lazy(() => import("./pages/ErrorPage"));

// Small fallback to avoid layout shift during lazy loads
const Fallback: React.FC = () => (
  <div className="h-screen flex items-center justify-center text-sm text-gray-500">
    <SuspenseLoader />
  </div>
);

type RouteConfig = {
  path: string;
  element: ReactElement;
  protected?: boolean;
  layout?: Partial<PageLayoutProps>; // allow passing subset of layout props (children supplied internally)
};

// Helper component to supply sanitized docId (path param wins over query param)
const VerificationPageWrapper: React.FC = () => {
  const params = useParams() as { docId?: string };
  const location = useLocation();
  const { logout } = useAuth();
  
  // Sanitize: keep only first segment before a slash or ? (defensive; router already delimits)
  const cleanDocId = useMemo(() => {
    if (!params.docId) return undefined;
    return params.docId.split(/[/?#]/, 1)[0];
  }, [params.docId]);

  // Store cleanDocId in localStorage user-data for performance
  React.useEffect(() => {
    if (cleanDocId) {
      try {
        const session = getAgentSession();
        if (session && session.docId !== cleanDocId) logout();
        // Read existing user-data or create new object
        const userDataRaw = localStorage.getItem('user-data');
        let userData = {};
        
        if (userDataRaw) {
          try {
            userData = JSON.parse(userDataRaw);
          } catch (parseError) {
            console.warn('[App] Failed to parse existing user-data, creating new:', parseError);
            userData = {};
          }
        }
        
        // Update docId in user-data
        const updatedUserData = { ...userData, docId: cleanDocId };
        localStorage.setItem('user-data', JSON.stringify(updatedUserData));
      } catch (error) {
        console.warn('[App] Failed to store docId in user-data:', error);
      }
    }
  }, [cleanDocId, logout]);

  // Create a key that changes when navigating back to force re-render
  // This ensures VerificationPage re-renders when navigated back to from support page
  const pageKey = useMemo(() => {
    return `${cleanDocId ?? 'default'}-${location.pathname}-${location.search}`;
  }, [cleanDocId, location.pathname, location.search]);

  return <VerificationPage key={pageKey} {...mockRootProps} docIdFromPath={cleanDocId} />;
};

// Public (unprotected) routes (order matters: explicit static root before param route handled via separate <Route>)
const publicRoutes: RouteConfig[] = [
  { path: "/:docId?", element: <VerificationPageWrapper /> },
  { path: "/support", element: <SupportPage /> },
  { path: "/error/:type?", element: <ErrorPage /> },
  { path : "/test", element: <AgentAffiliationFormPage generatePacketTimeoutMs={AXIOS_GENERATE_PACKET_TIMEOUT} />, layout: { className: "bg-white lg:bg-gray-200" }, }
];

// Protected routes (require auth)
const protectedRoutes: RouteConfig[] = [
  { path: "/welcome", element: <WelcomePage /> },
  {
    path: "/agent-affiliation",
    element: <AgentAffiliationFormPage generatePacketTimeoutMs={AXIOS_GENERATE_PACKET_TIMEOUT} />,
    layout: { className: "bg-white lg:bg-gray-200" },
  },
];

const renderWithLayout = (cfg: RouteConfig): ReactElement => {
  const content = <PageLayout {...cfg.layout}>{cfg.element}</PageLayout>;
  return cfg.protected ? <ProtectedRoute>{content}</ProtectedRoute> : content;
};

const App: React.FC = () => (
  <Suspense fallback={<Fallback />}>
    <Routes>
      {publicRoutes.map((r) => (
        <Route key={r.path} path={r.path} element={renderWithLayout(r)} />
      ))}
      {protectedRoutes.map((r) => (
        <Route
          key={r.path}
          path={r.path}
          element={<ProtectedRoute>{renderWithLayout(r)}</ProtectedRoute>}
        />
      ))}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  </Suspense>
);

export default App;